import type { Action, Thunk, Computed } from 'easy-peasy';
import { action, thunk, computed } from 'easy-peasy';
import type { ApplicationErrorOptions, ValidatedAddressMetadata } from '../../types';
import type { StoreModel } from '..';
import type {
  FetchZip3DistanceFilterParams,
  FulfillmentCenterCarrierSchedule,
  FulfillmentCenterConfig,
  GenerateGeolocationMetadataPayload,
} from '../services/FulfillmentConfigService/types';
import type { Injections } from '../services/injections';
import parseStatusCode from '../errors/parseStatusCode';
import type { Zip3Distance } from '../services/FulfillmentConfigService/types';
import type { Partner } from '../services/PartnerService/types';
import type { Schedule } from '../assets/types';
import { originAssetsToFulfillmentCenter } from './utils';
import type { PartnerAddressV2, PartnerContact, PartnerOrigin } from '@shipium-corp/shipium-ts-types';

export type FulfillmentCenterListOptions = 'v1' | 'v2' | 'all';
export interface FulfillmentModel {
  // data
  fulfillmentCenterConfigurations: Record<string, FulfillmentCenterConfig>;
  fulfillmentCenterList: Computed<FulfillmentModel, (v?: FulfillmentCenterListOptions) => any[]>;
  getFulfillmentConfigurationById: Computed<FulfillmentModel, (id: string) => FulfillmentCenterConfig | undefined>;
  lastAction?: number;
  saveFulfillmentCentersFailure: Action<FulfillmentModel, ApplicationErrorOptions>;
  // fetch
  fetchFulfillmentCenters: Thunk<FulfillmentModel, string | void, Injections, StoreModel, Promise<void>>;
  setFulfillmentCenters: Action<FulfillmentModel, FulfillmentCenterConfig[]>;
  fulfillmentActionStart: Action<FulfillmentModel, void>;
  fetchFulfillmentCentersFailure: Action<FulfillmentModel, ApplicationErrorOptions>;
  // validate address
  generateGeolocationMetadata: Thunk<
    FulfillmentModel,
    GenerateGeolocationMetadataPayload,
    Injections,
    StoreModel,
    Promise<ValidatedAddressMetadata>
  >;
  validateAddressFailure: Action<FulfillmentModel, ApplicationErrorOptions>;
  fetchZipDistances: Thunk<
    FulfillmentModel,
    FetchZip3DistanceFilterParams,
    Injections,
    StoreModel,
    Promise<Zip3Distance[]>
  >;
  setCarrierSchedules: Action<FulfillmentModel, FulfillmentCenterCarrierSchedule[]>;
  carrierSchedules: Record<string, FulfillmentCenterCarrierSchedule>;
  fetchCarrierSchedules: Thunk<
    FulfillmentModel,
    void,
    Injections,
    StoreModel,
    Promise<FulfillmentCenterCarrierSchedule[]>
  >;
}

export const fulfillmentModel: FulfillmentModel = {
  carrierSchedules: {},
  fulfillmentCenterConfigurations: {},
  fulfillmentCenterList: computed(
    [
      (state: any, storeState: any) => storeState?.partner?.activePartner,
      (state: any, storeState: any) => storeState?.origins?.origins,
      (state: any, storeState: any) => storeState?.address?.addresses,
      (state: any, storeState: any) => storeState?.schedule?.schedules,
      (state: any, storeState: any) => storeState?.contact?.contacts,
      (state: any, storeState: any) => state?.fulfillmentCenterConfigurations,
    ],
    (
      partner: Partner,
      origins: Record<string, PartnerOrigin>,
      addresses: Record<string, PartnerAddressV2>,
      schedules: Record<string, Schedule>,
      contacts: Record<string, PartnerContact>,
      fulfillmentCenterConfigurations: Record<string, FulfillmentCenterConfig>,
    ) => {
      return (forcedVersion?: FulfillmentCenterListOptions) => {
        const v1FCs = Object.values(fulfillmentCenterConfigurations ?? {})?.sort((a, b) => {
          if ((a.name || '') < (b.name || '')) {
            return -1;
          }
          if ((a.name || '') > (b.name || '')) {
            return 1;
          }
          return 0;
        });
        const v2FCs = originAssetsToFulfillmentCenter(origins, schedules, addresses, contacts);
        if (forcedVersion === 'all') {
          return [...v1FCs, ...v2FCs];
        } else if (forcedVersion === 'v2' || (!forcedVersion && partner?.featureToggles.UI_ORTHO_V2 === 'true')) {
          return v2FCs;
        } else {
          return v1FCs;
        }
      };
    },
  ),
  getFulfillmentConfigurationById: computed((state) => (id: string) => {
    const originMap = state.fulfillmentCenterList().reduce((prev, curr) => {
      prev[curr.fulfillmentCenterId] = curr;
      return prev;
    }, {});
    return originMap[id];
  }),
  saveFulfillmentCentersFailure: action((state, error) => {
    console.error(error);
  }),

  fetchFulfillmentCenters: thunk(async (actions, payload, { injections, getStoreState, getStoreActions }) => {
    try {
      actions.fulfillmentActionStart();
      const storeActions = getStoreActions();
      await Promise.all([
        storeActions.origins.fetchOrigins({}),
        storeActions.contact.fetchContacts({}),
        storeActions.address.fetchAddresses({}),
        storeActions.schedule.fetchSchedules({}),
      ]);
      return;
    } catch (error) {
      actions.fetchFulfillmentCentersFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: 'An error occured, unable to fetch Fulfillment Center Configurations.',
      });
      return error;
    }
  }),
  setFulfillmentCenters: action((state, fcConfigs) => {
    const configMap = fcConfigs.reduce<Record<string, FulfillmentCenterConfig>>((acc, curr) => {
      acc[curr.fulfillmentCenterId] = curr;
      return acc;
    }, {});
    state.fulfillmentCenterConfigurations = configMap;
  }),
  fulfillmentActionStart: action((state) => {
    state.lastAction = Date.now();
  }),
  fetchFulfillmentCentersFailure: action((state, error) => {
    console.error(error);
  }),
  generateGeolocationMetadata: thunk(async (actions, payload, { injections }) => {
    const { fulfillmentConfigService } = injections;
    const response = await fulfillmentConfigService.generateGeolocationMetadata(payload);
    return response;
  }),
  validateAddressFailure: action((state, error) => {
    console.error(error);
  }),
  fetchZipDistances: thunk(async (actions, payload, { injections }) => {
    try {
      const { fulfillmentConfigService } = injections;
      const response = await fulfillmentConfigService.fetchZip3Distances(payload);
      return response;
    } catch (error) {
      actions.fetchFulfillmentCentersFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: 'An error occured, unable to fetch valid',
      });
      return error;
    }
  }),
  setCarrierSchedules: action((state, payload) => {
    state.carrierSchedules = payload.reduce(
      (schedules, curr) => {
        schedules[curr.carrierScheduleId] = curr;
        return schedules;
      },
      { ...state.carrierSchedules },
    );
  }),
  fetchCarrierSchedules: thunk(async (actions, payload, { injections, getStoreState }) => {
    try {
      const storeState = getStoreState();
      const partnerId = storeState.session.user?.sessionPartnerId;
      if (!partnerId) {
        throw new Error('No logged in partner');
      }
      const res = await injections.fulfillmentConfigService.fetchCarrierSchedules(partnerId);
      actions.setCarrierSchedules(res);
      return res;
    } catch (error) {
      actions.fetchFulfillmentCentersFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: 'An error occured, unable to fetch carrier schedules',
      });
      return error;
    }
  }),
};
